import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Search,
  Star,
  ExternalLink,
  CheckCircle,
  BookOpen,
  Settings,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  FileText
} from 'lucide-react';
import { dsaProblemSets, dsaPatterns, dsaDifficulties, DSAProblemSet, DSAProblem } from '../../data/dsaData';
import { dsaService, DSAProblem as BackendDSAProblem } from '../../services/dsaService';
import { config } from '../../config/env';
import NoteEditor from '../../components/common/NoteEditor';
import PageHeader from '../../components/common/PageHeader';

interface ProblemProgress {
  problemId: string;
  isCompleted: boolean;
  isFavorited: boolean;
  completedAt?: Date;
  attempts: number;
  timeSpent: number; // in minutes
  notes?: string;
}

interface DSAStats {
  totalSolved: number;
  easyTotal: number;
  easySolved: number;
  mediumTotal: number;
  mediumSolved: number;
  hardTotal: number;
  hardSolved: number;
  currentStreak: number;
  bestStreak: number;
  totalTimeSpent: number;
  averageTimePerProblem: number;
}

interface PatternGroup {
  pattern: string;
  problems: DSAProblem[];
  totalProblems: number;
  solvedProblems: number;
  isExpanded: boolean;
}

const DSAPractice: React.FC = () => {
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();

  // Determine initial problem set from URL or default to 75
  const getInitialProblemSet = (): DSAProblemSet => {
    if (section === '150') {
      return dsaProblemSets.find(set => set.id === 'letsboostup-150') || dsaProblemSets[0];
    } else if (section === '250') {
      return dsaProblemSets.find(set => set.id === 'letsboostup-250') || dsaProblemSets[0];
    }
    return dsaProblemSets[0]; // Default to 75
  };

  const [selectedProblemSet, setSelectedProblemSet] = useState<DSAProblemSet>(getInitialProblemSet());
  const [problems, setProblems] = useState<DSAProblem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Removed unused state: savingProgress
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatterns, setSelectedPatterns] = useState<string[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>([]);
  // False means the catalog shows the full problem set. When enabled, the
  // status filter should include completed problems only.
  const [showCompleted, setShowCompleted] = useState(false);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [sortBy, setSortBy] = useState<'title' | 'difficulty' | 'pattern'>('pattern');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [progress, setProgress] = useState<Record<string, ProblemProgress>>({});
  const [showFilters, setShowFilters] = useState(false);
  const [expandedPatterns, setExpandedPatterns] = useState<Record<string, boolean>>({});
  const [viewMode, setViewMode] = useState<'grouped' | 'list'>('grouped');
  const [notesEditor, setNotesEditor] = useState<{ problemId: string; problemTitle: string; content: string } | null>(null);
  const [notesLoading, setNotesLoading] = useState<Record<string, boolean>>({});

  // Use a ref to prevent infinite loops between URL and state
  const isUpdatingFromUrl = React.useRef(false);

  // Map problem set ID to questionSet number
  const getQuestionSetNumber = (setId: string): number | null => {
    if (setId === 'letsboostup-75') return 75;
    if (setId === 'letsboostup-150') return 150;
    if (setId === 'letsboostup-250') return 250;
    return null;
  };

  // Fetch problems from backend when problem set changes
  useEffect(() => {
    const questionSet = getQuestionSetNumber(selectedProblemSet.id);

    if (questionSet) {
      // Fetch from backend
      setLoading(true);
      setError(null);

      dsaService.getProblems({
        questionSet,
        limit: 1000 // Get all problems for the set
      })
        .then((response) => {
          console.log('📥 Backend response:', {
            total: response.total,
            problemsReceived: response.problems.length,
            limit: response.limit,
            page: response.page
          });

          // Map backend problems to frontend format
          const mappedProblems: DSAProblem[] = response.problems.map((p: BackendDSAProblem) => ({
            id: p.id,
            title: p.title,
            difficulty: p.difficulty,
            pattern: p.pattern,
            category: p.category,
            leetcodeUrl: p.leetcodeUrl,
            description: p.description,
            approach: p.approach,
            timeComplexity: p.timeComplexity,
            spaceComplexity: p.spaceComplexity,
            companies: p.companies || [],
            tags: p.tags || [],
            isPremium: p.isPremium || false,
            categoryOrder: p.categoryOrder,
            orderInCategory: p.orderInCategory,
          }));

          console.log(`✅ Loaded ${mappedProblems.length} problems from backend (total: ${response.total})`);
          setProblems(mappedProblems);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching problems:', err);
          console.error('Error details:', {
            message: err.message,
            status: err.status,
            data: err.data,
            questionSet,
            url: `${config.API_BASE_URL}/dsa?questionSet=${questionSet}&limit=1000`
          });
          setError(`Failed to load problems from backend: ${err.message || 'Network error'}. Please check if the backend is running on port 4000.`);
          // Don't fallback to static data - show error instead
          setProblems([]);
          setLoading(false);
        });
    } else {
      // If questionSet is not recognized, show error
      setError(`Invalid problem set: ${selectedProblemSet.id}`);
      setProblems([]);
      setLoading(false);
    }
  }, [selectedProblemSet]);

  // Update problem set when URL section changes (sync FROM URL TO state)
  useEffect(() => {
    const newSet = getInitialProblemSet();
    if (newSet.id !== selectedProblemSet.id) {
      isUpdatingFromUrl.current = true; // Mark that we're updating from URL
      setSelectedProblemSet(newSet);
    }
    // Only depend on section - don't include selectedProblemSet.id to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  // Update URL when problem set changes (only if URL doesn't match AND not updating from URL)
  useEffect(() => {
    // Skip if we're updating from URL to prevent infinite loop
    if (isUpdatingFromUrl.current) {
      isUpdatingFromUrl.current = false;
      return;
    }

    const sectionFromSet = selectedProblemSet.id === 'letsboostup-75' ? '75' :
      selectedProblemSet.id === 'letsboostup-150' ? '150' :
        selectedProblemSet.id === 'letsboostup-250' ? '250' : '75';

    if (section !== sectionFromSet) {
      navigate(`/practice/dsa/${sectionFromSet}`, { replace: true });
    }
  }, [selectedProblemSet.id, navigate, section]);

  // Load progress from backend and localStorage on mount
  useEffect(() => {
    const loadProgress = async () => {
      // Load from localStorage first (for immediate UI update)
      const savedProgress = localStorage.getItem('dsa-progress');
      if (savedProgress) {
        try {
          const parsed = JSON.parse(savedProgress);
          setProgress(parsed);
        } catch (e) {
          console.error('Error parsing localStorage progress:', e);
        }
      }

      // Try to load from backend if user is authenticated
      try {
        const token = localStorage.getItem('access_token');
        if (token) {
          console.log('📊 Fetching progress from backend...');
          const backendProgress = await dsaService.getAllProgress();
          console.log('📊 Backend progress response:', backendProgress);

          if (backendProgress && Array.isArray(backendProgress)) {
            if (backendProgress.length === 0) {
              console.log('ℹ️ No progress data found in backend (user has not completed any problems yet)');
              // Keep localStorage data if available, otherwise start with empty progress
              return;
            }

            // Convert backend progress to frontend format
            const progressMap: Record<string, ProblemProgress> = {};
            backendProgress.forEach((p: any) => {
              progressMap[p.problemId] = {
                problemId: p.problemId,
                isCompleted: p.isCompleted || false,
                isFavorited: p.isFavorited || false,
                completedAt: p.completedAt ? new Date(p.completedAt) : undefined,
                attempts: p.attempts || 0,
                timeSpent: p.timeSpent || 0,
                notes: p.notes
              };
            });
            console.log(`✅ Loaded ${backendProgress.length} progress records from backend`);
            setProgress(progressMap);
            // Also save to localStorage for offline access
            localStorage.setItem('dsa-progress', JSON.stringify(progressMap));
          } else {
            console.warn('⚠️ Backend progress response is not an array:', backendProgress);
          }
        } else {
          console.log('ℹ️ No auth token found, using localStorage progress only');
        }
      } catch (err) {
        console.warn('⚠️ Could not load progress from backend (user may not be logged in):', err);
        // Continue with localStorage data
      }
    };

    loadProgress();

    // Initialize all patterns as expanded
    const initialExpanded: Record<string, boolean> = {};
    dsaPatterns.forEach((pattern: string) => {
      initialExpanded[pattern] = true;
    });
    setExpandedPatterns(initialExpanded);
  }, []);

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('dsa-progress', JSON.stringify(progress));
  }, [progress]);

  // Calculate stats - only use problems from backend, no fallback to static data
  const stats: DSAStats = useMemo(() => {
    // Only use problems fetched from backend
    const allProblems = problems;
    const completedProblems = Object.values(progress).filter((p: ProblemProgress) => p.isCompleted);

    const easyProblems = allProblems.filter((p: DSAProblem) => p.difficulty === 'Easy');
    const mediumProblems = allProblems.filter((p: DSAProblem) => p.difficulty === 'Medium');
    const hardProblems = allProblems.filter((p: DSAProblem) => p.difficulty === 'Hard');

    const easySolved = completedProblems.filter((p: ProblemProgress) => {
      const problem = allProblems.find((prob: DSAProblem) => prob.id === p.problemId);
      return problem?.difficulty === 'Easy';
    }).length;

    const mediumSolved = completedProblems.filter((p: ProblemProgress) => {
      const problem = allProblems.find((prob: DSAProblem) => prob.id === p.problemId);
      return problem?.difficulty === 'Medium';
    }).length;

    const hardSolved = completedProblems.filter((p: ProblemProgress) => {
      const problem = allProblems.find((prob: DSAProblem) => prob.id === p.problemId);
      return problem?.difficulty === 'Hard';
    }).length;

    return {
      totalSolved: completedProblems.length,
      easyTotal: easyProblems.length,
      easySolved,
      mediumTotal: mediumProblems.length,
      mediumSolved,
      hardTotal: hardProblems.length,
      hardSolved,
      currentStreak: 0,
      bestStreak: 0,
      totalTimeSpent: completedProblems.reduce((total: number, p: ProblemProgress) => total + p.timeSpent, 0),
      averageTimePerProblem: completedProblems.length > 0
        ? completedProblems.reduce((total: number, p: ProblemProgress) => total + p.timeSpent, 0) / completedProblems.length
        : 0
    };
  }, [progress, problems]);

  // Filter problems
  const filteredProblems = useMemo(() => {
    // Only use problems from backend, don't fallback to static data
    const problemsToFilter = problems;
    console.log('🔍 Filtering problems:', {
      problemsFromState: problems.length,
      usingBackendData: problems.length > 0,
      selectedProblemSet: selectedProblemSet.id,
      problemsToFilterCount: problemsToFilter.length
    });
    return problemsToFilter.filter((problem: DSAProblem) => {
      // Search filter
      if (searchTerm && !problem.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !problem.pattern.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Pattern filter
      if (selectedPatterns.length > 0 && !selectedPatterns.includes(problem.pattern)) {
        return false;
      }

      // Difficulty filter
      if (selectedDifficulties.length > 0 && !selectedDifficulties.includes(problem.difficulty)) {
        return false;
      }

      // Status filter
      const problemProgress = progress[problem.id];
      if (showCompleted && !problemProgress?.isCompleted) {
        return false;
      }

      // Favorites filter
      if (showFavoritesOnly && !problemProgress?.isFavorited) {
        return false;
      }

      return true;
    });
  }, [problems, selectedProblemSet, searchTerm, selectedPatterns, selectedDifficulties, showCompleted, showFavoritesOnly, progress]);

  // Group problems by pattern
  const groupedProblems = useMemo(() => {
    const groups: Record<string, PatternGroup> = {};

    filteredProblems.forEach((problem: DSAProblem) => {
      if (!groups[problem.pattern]) {
        groups[problem.pattern] = {
          pattern: problem.pattern,
          problems: [],
          totalProblems: 0,
          solvedProblems: 0,
          isExpanded: expandedPatterns[problem.pattern] || false
        };
      }

      groups[problem.pattern].problems.push(problem);
      groups[problem.pattern].totalProblems++;

      if (progress[problem.id]?.isCompleted) {
        groups[problem.pattern].solvedProblems++;
      }
    });

    // Preserve the curated learning sequence from the canonical DSA roadmap.
    // Difficulty remains a label here; List view retains its explicit sort control.
    Object.values(groups).forEach(group => {
      group.problems.sort((a, b) => {
        const orderA = a.orderInCategory ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.orderInCategory ?? Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) return orderA - orderB;
        return a.title.localeCompare(b.title);
      });
    });

    // Order groups by the Blind 75 roadmap (dsaPatterns), with any unknown
    // patterns falling back to the end in alphabetical order.
    return Object.values(groups).sort((a, b) => {
      const categoryOrderA = a.problems[0]?.categoryOrder;
      const categoryOrderB = b.problems[0]?.categoryOrder;
      if (categoryOrderA !== undefined && categoryOrderB !== undefined && categoryOrderA !== categoryOrderB) {
        return categoryOrderA - categoryOrderB;
      }
      const ia = dsaPatterns.indexOf(a.pattern);
      const ib = dsaPatterns.indexOf(b.pattern);
      const ra = ia === -1 ? Number.MAX_SAFE_INTEGER : ia;
      const rb = ib === -1 ? Number.MAX_SAFE_INTEGER : ib;
      if (ra !== rb) return ra - rb;
      return a.pattern.localeCompare(b.pattern);
    });
  }, [filteredProblems, expandedPatterns, progress]);

  const toggleProblemComplete = async (problemId: string, e?: React.MouseEvent) => {
    // Prevent event propagation to parent elements
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const currentState = progress[problemId]?.isCompleted || false;
    const newState = !currentState;

    // Optimistically update UI
    setProgress(prev => ({
      ...prev,
      [problemId]: {
        ...prev[problemId],
        problemId,
        isCompleted: newState,
        isFavorited: prev[problemId]?.isFavorited || false,
        completedAt: newState ? new Date() : undefined,
        attempts: (prev[problemId]?.attempts || 0) + (newState ? 1 : 0),
        timeSpent: prev[problemId]?.timeSpent || 0
      }
    }));

    // Save to localStorage immediately
    const updatedProgress = {
      ...progress,
      [problemId]: {
        ...progress[problemId],
        problemId,
        isCompleted: newState,
        isFavorited: progress[problemId]?.isFavorited || false,
        completedAt: newState ? new Date() : undefined,
        attempts: (progress[problemId]?.attempts || 0) + (newState ? 1 : 0),
        timeSpent: progress[problemId]?.timeSpent || 0
      }
    };
    localStorage.setItem('dsa-progress', JSON.stringify(updatedProgress));

    // Save to backend if user is authenticated
    // Removed savingProgress state tracking
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await dsaService.updateProgress(problemId, {
          isCompleted: newState,
          timeSpent: updatedProgress[problemId].timeSpent
        });
        console.log('✅ Progress saved to backend:', problemId, newState);
      }
    } catch (err: any) {
      console.error('❌ Failed to save progress to backend:', err);
      // Don't revert UI - localStorage is already updated
    } finally {
      // Removed savingProgress state tracking
    }
  };

  const toggleProblemFavorite = async (problemId: string, e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const currentState = progress[problemId]?.isFavorited || false;
    const newState = !currentState;

    // Optimistically update UI
    setProgress(prev => ({
      ...prev,
      [problemId]: {
        ...prev[problemId],
        problemId,
        isCompleted: prev[problemId]?.isCompleted || false,
        isFavorited: newState,
        attempts: prev[problemId]?.attempts || 0,
        timeSpent: prev[problemId]?.timeSpent || 0
      }
    }));

    // Save to localStorage
    const updatedProgress = {
      ...progress,
      [problemId]: {
        ...progress[problemId],
        problemId,
        isCompleted: progress[problemId]?.isCompleted || false,
        isFavorited: newState,
        attempts: progress[problemId]?.attempts || 0,
        timeSpent: progress[problemId]?.timeSpent || 0
      }
    };
    localStorage.setItem('dsa-progress', JSON.stringify(updatedProgress));

    // Save to backend if authenticated
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await dsaService.toggleFavorite(problemId);
        console.log('✅ Favorite status saved to backend:', problemId, newState);
      }
    } catch (err: any) {
      console.error('❌ Failed to save favorite status to backend:', err);
    }
  };

  const togglePatternExpanded = (pattern: string) => {
    setExpandedPatterns(prev => ({
      ...prev,
      [pattern]: !prev[pattern]
    }));
  };

  const handleOpenNotes = async (problemId: string, problemTitle: string) => {
    setNotesLoading(prev => ({ ...prev, [problemId]: true }));
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        alert('Please login to add notes');
        return;
      }

      try {
        const notesData = await dsaService.getNotes(problemId);
        setNotesEditor({
          problemId,
          problemTitle,
          content: notesData?.notes || '',
        });
      } catch (error: any) {
        // If notes don't exist (404), create empty editor
        if (error.status === 404) {
          setNotesEditor({
            problemId,
            problemTitle,
            content: '',
          });
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error('Error loading notes:', error);
      alert('Failed to load notes. Please try again.');
    } finally {
      setNotesLoading(prev => ({ ...prev, [problemId]: false }));
    }
  };

  const handleSaveNotes = async (content: string) => {
    if (!notesEditor) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      throw new Error('Please login to save notes');
    }

    try {
      // Try to update first, if fails, create new
      try {
        await dsaService.updateNotes(notesEditor.problemId, content, 'markdown');
      } catch (error: any) {
        if (error.status === 404) {
          // Notes don't exist, create new
          await dsaService.createNotes(notesEditor.problemId, content, 'markdown');
        } else {
          throw error;
        }
      }

      // Update local state
      setNotesEditor(prev => prev ? { ...prev, content } : null);
    } catch (error) {
      console.error('Error saving notes:', error);
      throw error;
    }
  };

  const resetProgress = () => {
    if (window.confirm('Are you sure you want to reset all progress? This action cannot be undone.')) {
      setProgress({});
      localStorage.removeItem('dsa-progress');
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-emerald-400 bg-emerald-500/10 border border-emerald-500/20';
      case 'Medium': return 'text-amber-400 bg-amber-500/10 border border-amber-500/20';
      case 'Hard': return 'text-rose-400 bg-rose-500/10 border border-rose-500/20';
      default: return 'text-gray-400 bg-gray-500/10 border border-gray-500/20';
    }
  };

  const getPatternColor = (_pattern: string) => {
    // Pattern labels use the shared platform accent instead of a separate hue.
    return 'bg-primary-50 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300';
  };

  return (
    <div className="nav-tab-page practice-detail-page dsa-catalog-page min-h-screen pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <PageHeader
          title="Practice DSA by pattern"
          description="Work through curated problem sets organized by pattern — build consistency, track what you have solved, and close gaps before interviews."
          icon={BookOpen}
          iconColor="text-primary-600 dark:text-primary-400"
          gradientColors={{ from: 'from-primary-500', via: 'via-primary-600', to: 'to-primary-700' }}
        />

        {/* Problem Set Selection */}
        <div className="mb-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {dsaProblemSets.map((problemSet: DSAProblemSet) => {
              const isSelected = selectedProblemSet.id === problemSet.id;

              return (
                <button
                  key={problemSet.id}
                  onClick={() => setSelectedProblemSet(problemSet)}
                  className={`ds-surface-card relative p-5 rounded-xl text-left transition-all duration-200 border overflow-hidden ${isSelected
                    ? 'border-primary-500/50 bg-white dark:bg-gray-800 ring-1 ring-primary-500/20 shadow-md'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-md hover:border-gray-300 dark:hover:border-gray-700 hover:shadow-lg'
                    }`}
                >
                  {/* Selected indicator */}
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-primary-500" />
                  )}

                  {/* Header row: title + duration */}
                  <div className="flex items-center justify-between mb-1.5">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      {problemSet.name}
                    </h3>
                    <span className="text-[11px] text-gray-500 dark:text-gray-500 px-2 py-0.5 bg-gray-50 dark:bg-gray-800/80 rounded font-medium">
                      {problemSet.duration}
                    </span>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed mb-4">
                    {problemSet.description}
                  </p>

                  {/* Footer: total problems + difficulty breakdown */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-bold text-gray-900 dark:text-white">{problemSet.totalProblems}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-500">problems</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="dsa-set-stat text-[11px] font-medium px-1.5 py-0.5 rounded">
                        {problemSet.difficulty.easy}E
                      </span>
                      <span className="dsa-set-stat text-[11px] font-medium px-1.5 py-0.5 rounded">
                        {problemSet.difficulty.medium}M
                      </span>
                      <span className="dsa-set-stat text-[11px] font-medium px-1.5 py-0.5 rounded">
                        {problemSet.difficulty.hard}H
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Progress Overview */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-md p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Progress
            </h3>
            <button
              onClick={resetProgress}
              className="flex items-center space-x-1.5 px-3 py-1.5 text-xs text-gray-400 hover:text-rose-400 rounded-lg transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>Reset</span>
            </button>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Circular Progress */}
            <div className="relative w-36 h-36 shrink-0">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="52" fill="none" strokeWidth="10" className="stroke-gray-200 dark:stroke-gray-700" />
                <circle
                  cx="60" cy="60" r="52" fill="none" strokeWidth="10"
                  strokeLinecap="round"
                  className="stroke-primary-500"
                  strokeDasharray={`${2 * Math.PI * 52}`}
                  strokeDashoffset={`${2 * Math.PI * 52 * (1 - (problems.length > 0 ? stats.totalSolved / problems.length : 0))}`}
                  style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-bold text-gray-900 dark:text-white">
                  {problems.length > 0 ? Math.round((stats.totalSolved / problems.length) * 100) : 0}%
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {stats.totalSolved} / {problems.length}
                </span>
              </div>
            </div>

            {/* Difficulty Breakdown */}
            <div className="flex-1 w-full space-y-4">
              <div className="flex items-center gap-3">
                <span className="dsa-progress-label text-xs font-medium w-14">Easy</span>
                <div className="flex-1 h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all duration-500"
                    style={{ width: `${stats.easyTotal > 0 ? (stats.easySolved / stats.easyTotal) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400 w-14 text-right font-medium">{stats.easySolved}/{stats.easyTotal}</span>
              </div>

              <div className="flex items-center gap-3">
                <span className="dsa-progress-label text-xs font-medium w-14">Medium</span>
                <div className="flex-1 h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-600 rounded-full transition-all duration-500"
                    style={{ width: `${stats.mediumTotal > 0 ? (stats.mediumSolved / stats.mediumTotal) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400 w-14 text-right font-medium">{stats.mediumSolved}/{stats.mediumTotal}</span>
              </div>

              <div className="flex items-center gap-3">
                <span className="dsa-progress-label text-xs font-medium w-14">Hard</span>
                <div className="flex-1 h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-700 rounded-full transition-all duration-500"
                    style={{ width: `${stats.hardTotal > 0 ? (stats.hardSolved / stats.hardTotal) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-xs text-gray-600 dark:text-gray-400 w-14 text-right font-medium">{stats.hardSolved}/{stats.hardTotal}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="card p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filters & Controls</h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">View:</span>
                <button
                  onClick={() => setViewMode('grouped')}
                  className={`px-3 py-1 rounded text-sm ${viewMode === 'grouped'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                >
                  Grouped
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1 rounded text-sm ${viewMode === 'list'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                >
                  List
                </button>
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <Settings className="w-4 h-4" />
                <span>{showFilters ? 'Hide' : 'Show'} Filters</span>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search problems..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10 w-full"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowCompleted(!showCompleted)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showCompleted
                  ? 'bg-primary-100 text-primary-800 dark:bg-primary-500/15 dark:text-primary-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}
              >
                {showCompleted ? 'Showing Completed' : 'Show Completed'}
              </button>

              <button
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showFavoritesOnly
                  ? 'bg-primary-100 text-primary-800 dark:bg-primary-500/15 dark:text-primary-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}
              >
                <Star className="w-4 h-4 mr-1 inline" />
                Starred
              </button>
            </div>
          </div>

          {showFilters && (
            <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
              {/* Pattern Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Patterns
                </label>
                <div className="flex flex-wrap gap-2">
                  {dsaPatterns.map((pattern: string) => (
                    <button
                      key={pattern}
                      onClick={() => {
                        setSelectedPatterns(prev =>
                          prev.includes(pattern)
                            ? prev.filter(p => p !== pattern)
                            : [...prev, pattern]
                        );
                      }}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedPatterns.includes(pattern)
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                      {pattern}
                    </button>
                  ))}
                </div>
              </div>

              {/* Difficulty Filter */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Difficulty
                </label>
                <div className="flex gap-2">
                  {dsaDifficulties.map((difficulty: string) => (
                    <button
                      key={difficulty}
                      onClick={() => {
                        setSelectedDifficulties(prev =>
                          prev.includes(difficulty)
                            ? prev.filter(d => d !== difficulty)
                            : [...prev, difficulty]
                        );
                      }}
                      className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${selectedDifficulties.includes(difficulty)
                        ? getDifficultyColor(difficulty)
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                    >
                      {difficulty}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="card p-6 mb-8">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-gray-600 dark:text-gray-400">Loading problems...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="card p-6 mb-8 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center space-x-2 text-yellow-800 dark:text-yellow-200">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Problems Display */}
        {viewMode === 'grouped' ? (
          <div className="space-y-6">
            {groupedProblems.map((group) => (
              <div key={group.pattern} className="card overflow-hidden">
                {/* Category Header */}
                <div
                  className="px-4 py-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => togglePatternExpanded(group.pattern)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {group.pattern}
                      </span>
                      <span className="text-xs text-gray-600 dark:text-gray-400">
                        {group.solvedProblems}/{group.totalProblems} solved
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div
                          className="bg-primary-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${(group.solvedProblems / group.totalProblems) * 100}%` }}
                        />
                      </div>
                      {group.isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                  </div>
                </div>

                {/* Problem Rows */}
                {group.isExpanded && (
                  <div>
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider w-12">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">Problem</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider w-24">Difficulty</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider w-20">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {group.problems.map((problem) => {
                          const problemProgress = progress[problem.id];
                          const isCompleted = problemProgress?.isCompleted || false;
                          const isFavorited = problemProgress?.isFavorited || false;

                          return (
                            <tr
                              key={problem.id}
                              className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${isCompleted ? 'bg-green-50 dark:bg-green-900/10' : ''}`}
                            >
                              <td className="px-4 py-4 whitespace-nowrap">
                                <button
                                  onClick={(e) => toggleProblemComplete(problem.id, e)}
                                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isCompleted
                                    ? 'bg-green-500 border-green-500 text-white'
                                    : 'border-gray-300 dark:border-gray-600 hover:border-green-500'
                                    }`}
                                  title={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                                >
                                  {isCompleted && <CheckCircle className="w-4 h-4" />}
                                </button>
                              </td>

                              <td className="px-4 py-4">
                                <a
                                  href={problem.leetcodeUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline"
                                >
                                  {problem.title}
                                </a>
                              </td>

                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs font-medium rounded-lg ${getDifficultyColor(problem.difficulty)}`}>
                                  {problem.difficulty}
                                </span>
                              </td>

                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleOpenNotes(problem.id, problem.title);
                                    }}
                                    className="p-1 text-gray-400 hover:text-primary-400 transition-colors"
                                    title="Add notes"
                                  >
                                    <FileText className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={(e) => toggleProblemFavorite(problem.id, e)}
                                    className={`p-1 rounded transition-colors ${isFavorited
                                      ? 'text-yellow-500 hover:text-yellow-600'
                                      : 'text-gray-400 hover:text-yellow-500'
                                      }`}
                                  >
                                    <Star className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
                                  </button>
                                  <a
                                    href={problem.leetcodeUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-1 text-primary-400 hover:text-primary-300 transition-colors"
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </a>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}

            {groupedProblems.length === 0 && (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  No problems found matching your criteria
                </p>
              </div>
            )}
          </div>
        ) : (
          // List View - Table format
          <div className="card overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Problems ({filteredProblems.length})
                </h3>

                <div className="flex items-center space-x-4">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                    className="input-field text-sm"
                  >
                    <option value="title">Sort by Title</option>
                    <option value="difficulty">Sort by Difficulty</option>
                    <option value="pattern">Sort by Pattern</option>
                  </select>

                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    {sortOrder === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Problem
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Difficulty
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Pattern
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredProblems
                    .sort((a: DSAProblem, b: DSAProblem) => {
                      let comparison = 0;
                      switch (sortBy) {
                        case 'title':
                          comparison = a.title.localeCompare(b.title);
                          break;
                        case 'difficulty':
                          const difficultyOrder: Record<string, number> = { 'Easy': 1, 'Medium': 2, 'Hard': 3 };
                          comparison = difficultyOrder[a.difficulty] - difficultyOrder[b.difficulty];
                          break;
                        case 'pattern':
                          comparison = a.pattern.localeCompare(b.pattern);
                          break;
                      }
                      return sortOrder === 'asc' ? comparison : -comparison;
                    })
                    .map((problem: DSAProblem) => {
                      const problemProgress = progress[problem.id];
                      const isCompleted = problemProgress?.isCompleted || false;
                      const isFavorited = problemProgress?.isFavorited || false;

                      return (
                        <tr
                          key={problem.id}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${isCompleted ? 'bg-green-50 dark:bg-green-900/10' : ''
                            }`}
                        >
                          <td className="px-4 py-4 whitespace-nowrap">
                            <button
                              onClick={(e) => toggleProblemComplete(problem.id, e)}
                              className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${isCompleted
                                ? 'bg-green-500 border-green-500 text-white'
                                : 'border-gray-300 dark:border-gray-600 hover:border-green-500'
                                }`}
                              title={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                            >
                              {isCompleted && <CheckCircle className="w-4 h-4" />}
                            </button>
                          </td>

                          <td className="px-4 py-4">
                            <div className="flex items-center space-x-3">
                              <div>
                                <a
                                  href={problem.leetcodeUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline"
                                >
                                  {problem.title}
                                </a>
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  {problem.category}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-lg ${getDifficultyColor(problem.difficulty)}`}>
                              {problem.difficulty}
                            </span>
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-lg ${getPatternColor(problem.pattern)}`}>
                              {problem.pattern}
                            </span>
                          </td>

                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleOpenNotes(problem.id, problem.title);
                                }}
                                className="p-1 text-gray-400 hover:text-primary-400 transition-colors"
                                title="Add notes"
                              >
                                <FileText className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => toggleProblemFavorite(problem.id, e)}
                                className={`p-1 rounded transition-colors ${isFavorited
                                  ? 'text-yellow-500 hover:text-yellow-600'
                                  : 'text-gray-400 hover:text-yellow-500'
                                  }`}
                              >
                                <Star className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`} />
                              </button>

                              <a
                                href={problem.leetcodeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1 text-primary-400 hover:text-primary-300 transition-colors"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {filteredProblems.length === 0 && (
              <div className="text-center py-12">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  No problems found matching your criteria
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Notes Editor Modal */}
      {notesEditor && (
        <NoteEditor
          problemId={notesEditor.problemId}
          problemTitle={notesEditor.problemTitle}
          initialContent={notesEditor.content}
          onSave={handleSaveNotes}
          onClose={() => setNotesEditor(null)}
          isLoading={notesLoading[notesEditor.problemId]}
        />
      )}
    </div>
  );
};

export default DSAPractice;
